import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πώς γίνεται ένα custom τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ξεκινά από consultation, συζήτηση για την ιδέα, το placement και την αισθητική. Μετά ο artist σχεδιάζει το custom design, γίνονται τυχόν προσαρμογές, και η συνεδρία ακολουθεί." } },
    { "@type": "Question", name: "Χρειάζεται να έχω έτοιμο σχέδιο για custom τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Αρκεί μια ιδέα, εικόνα, έννοια ή αίσθηση. Ο artist αναλαμβάνει να τη μετατρέψει σε λειτουργικό design πάνω στο σώμα." } },
    { "@type": "Question", name: "Πόσο χρόνο παίρνει ένα custom τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από την πολυπλοκότητα. Το consultation και ο σχεδιασμός μπορεί να χρειαστούν 1–2 εβδομάδες πριν τη συνεδρία. Η ίδια η συνεδρία εξαρτάται από μέγεθος και λεπτομέρεια." } },
  ],
};

export default function CustomTattooProcessAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πώς γίνεται ένα custom τατουάζ από την ιδέα μέχρι το σχέδιο",
      "description": "Η πλήρης διαδικασία πίσω από ένα custom τατουάζ: από την ιδέα στο concept, τον σχεδιασμό, την προσαρμογή στο σώμα και την ημέρα της συνεδρίας.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-25",
      "dateModified": "2026-04-25",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/custom-tattoo-process-athens",
      "inLanguage": "el"
    };
    inject('schema-custom-process-faq', faqSchema);
    inject('schema-custom-process-article', articleSchema);
    return () => {
      document.getElementById('schema-custom-process-faq')?.remove();
      document.getElementById('schema-custom-process-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πώς Γίνεται ένα Custom Τατουάζ: Από την Ιδέα στο Σχέδιο | 210 Tattoo Athens</title>
        <meta name="description" content="Η διαδικασία πίσω από ένα custom τατουάζ: consultation, σχεδιασμός, προσαρμογή στο σώμα και η ημέρα της συνεδρίας, βήμα προς βήμα." />
        <link rel="canonical" href="https://210tattoo.gr/blog/custom-tattoo-process-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/custom-tattoo-process-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/custom-tattoo-process-athens" />
        <meta property="og:title" content="Πώς Γίνεται ένα Custom Τατουάζ | 210 Tattoo Athens" />
        <meta property="og:description" content="Από ιδέα σε τατουάζ: η πλήρης διαδικασία custom design στο 210 Tattoo Athens." />
        <meta property="og:url" content="https://210tattoo.gr/blog/custom-tattoo-process-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('custom-tattoo-process-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πώς Γίνεται Custom Τατουάζ | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Η διαδικασία custom τατουάζ: από την ιδέα μέχρι τη συνεδρία." />
        <meta name="twitter:image" content={ogImage('custom-tattoo-process-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Custom Τατουάζ Διαδικασία</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Η διαδικασία</span>
              <br />
              <span className="text-white">πίσω από ένα custom τατουάζ</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">25 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Ένα custom τατουάζ δεν ξεκινά από ένα έτοιμο σχέδιο. Ξεκινά από μια ιδέα, και η δουλειά του artist είναι να τη μετατρέψει σε λειτουργικό design πάνω στο σώμα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Idea to concept */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Από την ιδέα <span className="text-pink-500">στο concept</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η πρώτη φάση είναι η κατανόηση. Τι θέλεις να εκφράσεις, πόσο προσωπικό είναι το σχέδιο, και τι αισθητική σε εκφράζει.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Σε αυτό το στάδιο πολλές φορές η αρχική ιδέα αλλάζει μορφή ώστε να γίνει πιο λειτουργική ως τατουάζ. Αυτό είναι αναμενόμενο και αποτελεί κομμάτι της δουλειάς.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Η ιδέα δεν χρειάζεται να είναι τελειωμένη', desc: 'Μπορεί να είναι μια εικόνα, μια έννοια, μια εμπειρία ή κάτι πολύ αφηρημένο. Αρκεί ένα σημείο εκκίνησης.' },
                { title: 'Το consultation αποκαλύπτει το concept', desc: 'Μέσα από συζήτηση, η αρχική ιδέα αναπτύσσεται σε κάτι που έχει καθαρή οπτική κατεύθυνση.' },
                { title: 'Η αισθητική σου μετράει', desc: 'Minimal, detailed, graphic, organic, ποιο style σε αντιπροσωπεύει καθορίζει πώς θα αποδοθεί η ιδέα.' },
                { title: 'Μερικές φορές η ιδέα αλλάζει', desc: 'Αν κάτι δεν λειτουργεί ως τατουάζ στο συγκεκριμένο placement, ο artist θα σου εξηγήσει γιατί και τι εναλλακτικές υπάρχουν.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Design and adaptation */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σχεδιασμός <span className="text-pink-500">και προσαρμογή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το δεύτερο στάδιο είναι ο σχεδιασμός. Εδώ το concept μετατρέπεται σε οπτικό σχέδιο.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Γίνεται επιλογή στυλ, σύνθεσης, γραμμών και ρυθμού του design. Ο σχεδιασμός τατουάζ έχει τις δικές του απαιτήσεις: το αντικείμενο θα ζει πάνω στο σώμα για πάντα, και αυτό καθορίζει κάθε επιλογή.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Επιλογή στυλ', desc: 'Το concept μπορεί να αποδοθεί σε διάφορα styles. Ο artist βοηθά να βρεθεί αυτό που ταιριάζει στην αισθητική σου και στις τεχνικές απαιτήσεις του σχεδίου.' },
                { title: 'Σύνθεση', desc: 'Πώς τοποθετούνται τα στοιχεία μεταξύ τους, πού πέφτει το βάρος οπτικά και ποιο είναι το focal point, αυτά καθορίζουν αν το τελικό αποτέλεσμα διαβάζεται σωστά.' },
                { title: 'Ρυθμός και ισορροπία', desc: 'Ένα καλό design έχει ρυθμό, τα στοιχεία του «οδηγούν» το μάτι φυσικά από ένα σημείο στο άλλο.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Body and placement */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Placement <span className="text-pink-500">και σώμα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Το σώμα δεν είναι καμβάς επίπεδος. Έχει κίνηση, καμπύλες και όρια.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ένα σωστό custom τατουάζ προσαρμόζεται πάνω σε αυτά τα στοιχεία και δεν τα αγνοεί. Ένα σχέδιο που λειτουργεί τέλεια σε επίπεδη επιφάνεια μπορεί να μοιάζει διαφορετικά πάνω στο σώμα, και ο artist πρέπει να το υπολογίζει.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Καμπύλες και τριδιάστατη επιφάνεια', desc: 'Το σώμα δεν είναι ευθύγραμμο. Ο σχεδιασμός πρέπει να λαμβάνει υπόψη πώς θα φαίνεται το σχέδιο όταν το μέλος κινείται.' },
                { title: 'Ροή με τη γραμμή του σώματος', desc: 'Τα καλύτερα custom tattoos φαίνεται να «ανήκουν» στο σώμα, όχι να έχουν κολληθεί πάνω του.' },
                { title: 'Αναλογίες βάσει placement', desc: 'Ένα σχέδιο που είναι ιδανικό σε μέγεθος για τον πήχη μπορεί να χρειαστεί αναλογικές αλλαγές για τον μηρό ή το πλευρό.' },
                { title: 'Μελλοντικά expansion', desc: 'Αν υπάρχει πιθανότητα να θέλεις να επεκτείνεις το τατουάζ αργότερα, ο σχεδιασμός λαμβάνει υπόψη αυτό το ενδεχόμενο.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process steps */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Βήματα <span className="text-pink-500">της διαδικασίας</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Από την πρώτη επικοινωνία μέχρι το τελικό αποτέλεσμα, η διαδικασία έχει συγκεκριμένα στάδια που δεν παραλείπονται.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Πρώτη επικοινωνία και συζήτηση ιδέας', desc: 'Μέσω Instagram ή consultation, περιγράφεις την ιδέα σου, το επιθυμητό placement και στέλνεις τυχόν references.' },
                { step: '02', title: 'Σχεδιασμός του custom design', desc: 'Ο artist φτιάχνει το σχέδιο βάσει αυτών που συζητήθηκαν. Υπάρχει χώρος για ανατροφοδότηση και μικρές αλλαγές πριν τη συνεδρία.' },
                { step: '03', title: 'Τελικές προσαρμογές και έλεγχος', desc: 'Πριν τη συνεδρία γίνεται τελικός έλεγχος, ισορροπία, αναλογίες, ροή. Αυτό το στάδιο είναι κρίσιμο για το τελικό αποτέλεσμα.' },
                { step: '04', title: 'Η ημέρα της συνεδρίας', desc: 'Εφαρμογή stencil, τελική επιβεβαίωση placement και εκτέλεση. Η σωστή προετοιμασία σημαίνει ότι η συνεδρία ξεκινά χωρίς εκπλήξεις.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What to ask in consultation */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να ρωτήσεις <span className="text-pink-500">στο consultation</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το consultation λειτουργεί ως συνεργασία μεταξύ σου και του artist. Μερικές ερωτήσεις βοηθούν να φύγεις με σαφή εικόνα για το πώς θα εξελιχθεί το project.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Πότε θα δω το σχέδιο;', desc: 'Σε κάποια studios το σχέδιο είναι έτοιμο πριν τη συνεδρία, σε άλλα αποκαλύπτεται την ημέρα. Αξίζει να ξέρεις τι να περιμένεις ώστε να μην υπάρχουν εκπλήξεις.' },
                { title: 'Τι γίνεται αν θέλω αλλαγές;', desc: 'Κατανόησε από πριν πόσες αναθεωρήσεις είναι εφικτές και σε ποιο σημείο της διαδικασίας μπορούν να γίνουν. Αλλαγές πάνω στο stencil την ημέρα της συνεδρίας είναι πιο περιοριστικές.' },
                { title: 'Πού ακριβώς θα τοποθετηθεί το στένσιλ;', desc: 'Το placement επηρεάζει πώς θα φαίνεται το τατουάζ σε ηρεμία και σε κίνηση. Η συζήτηση γι\' αυτό πριν τη συνεδρία αποφεύγει τυχόν μεταμέλεια στην τοποθέτηση.' },
                { title: 'Πόσο χρόνο θα πάρει η συνεδρία;', desc: 'Η εκτίμηση για τη διάρκεια βοηθά να οργανωθείς σωστά, να φας καλά πριν, και να μην έχεις άλλες υποχρεώσεις αμέσως μετά.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Custom τατουάζ <span className="text-pink-500">στο 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Κάθε custom project περνάει από διαδικασία συζήτησης και σχεδιασμού πριν την εκτέλεση. Στόχος είναι το τελικό τατουάζ να έχει λόγο ύπαρξης, και όχι απλά να είναι ένα ωραίο σχέδιο.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Δεν κάνουμε copy-paste references', desc: 'Κάθε σχέδιο είναι φτιαγμένο εξαρχής για εσένα. Τα references είναι αφετηρία, όχι το τελικό προϊόν.' },
                { title: 'Consultation χωρίς βιασύνη', desc: 'Αφιερώνουμε χρόνο στο να καταλάβουμε τι θέλεις. Δεν κλείνουμε ραντεβού αν δεν υπάρχει σαφές concept.' },
                { title: 'Ανατροφοδότηση πριν τη συνεδρία', desc: 'Μπορείς να δεις το σχέδιο πριν την ημέρα και να ζητήσεις αλλαγές αν χρειαστεί.' },
                { title: 'Αποτέλεσμα που αντέχει', desc: 'Σχεδιάζουμε με γνώμονα πώς θα δείχνει το τατουάζ μετά την επούλωση, και μετά από χρόνια.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έχεις ιδέα για custom τατουάζ; <span className="text-pink-500">Ας τη σχεδιάσουμε μαζί.</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας μήνυμα στο Instagram του 210 Tattoo Athens. Δεν χρειάζεται να έχεις έτοιμο σχέδιο, αρκεί να ξεκινήσεις τη συζήτηση.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/first-tattoo-design-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να Επιλέξεις Σχέδιο για Πρώτο Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Προσωπικό νόημα, custom design και τα πιο συχνά λάθη αρχαρίων.</p>
              </Link>
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να Διαλέξεις Tattoo Artist</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Portfolio, υγιεινή, consultation και red flags που δεν πρέπει να αγνοείς.</p>
              </Link>
              <Link to="/blog/fotografia-se-tatouaz-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς Μετατρέπεις Φωτογραφία σε Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Η καλλιτεχνική μετάφραση μιας φωτογραφίας σε σχέδιο τατουάζ.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
